import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Button, TextField, Chip,
  ToggleButton, ToggleButtonGroup, CircularProgress, Alert, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Switch, FormControlLabel,
  alpha,
} from '@mui/material';
import {
  EmojiEvents, Refresh, Settings as SettingsIcon,
  Casino as CasinoIcon, SportsEsports, AccountBalanceWallet,
  Groups, Schedule, MilitaryTech, Toll as SlotIcon, RocketLaunch as CrashIcon,
  Percent as PercentIcon, EventSeat as SeatIcon,
  PowerSettingsNew as PowerIcon,
} from '@mui/icons-material';
import { apiFetch } from '../utils/api';
import { C, StatCard, fmtNum } from './admin-dashboard/shared';

/* ──────────────────────────────────────────────────────────────
   Promotions — Daily / Weekly wager races
   Views: Leaderboard (POST /pramotion/leaderboard) · Config (GET/POST /pramotion/config)
   ────────────────────────────────────────────────────────────── */

type RaceType = 'daily' | 'weekly';
type TabKey = 'leaderboard' | 'config';

interface LeaderboardRow {
  rank: number;
  user_id: number | string;
  name: string;
  amount: number;
  points: number;
  is_boat?: boolean;
}
interface LeaderboardData {
  race_id: number;
  type: RaceType;
  start_time: string;
  end_time: string;
  multiplier: number;
  leaderboard: LeaderboardRow[];
}

interface RankPreview { rank: number; percentage: number; amount: number }
interface RaceConfig {
  type: RaceType;
  sports_point: number;
  casino_point: number;
  slot_point: number;
  crash_point: number;
  amount: number;
  winner_count: number;
  platform_fee_percent: number;
  top3_percentage: number;
  other_percentage: number;
  rank_percentage: Array<{ rank: number; percentage: number; amount: number }>;
  booked_seat_enabled: boolean;
  booked_seats: number[] | string;
  enabled: boolean;
}

/* Current IST (+05:30) ISO string — backend resolves the active race window from it. */
function istNowISO(): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).formatToParts(new Date());
  const p: Record<string, string> = {};
  parts.forEach((x) => { p[x.type] = x.value; });
  const hour = p.hour === '24' ? '00' : p.hour;
  return `${p.year}-${p.month}-${p.day}T${hour}:${p.minute}:${p.second}+05:30`;
}

/* booked_seats may arrive as a real array (jsonb) or a JSON string (text col) —
   normalise to a unique, ascending list of valid ranks (≥ 1). */
function parseSeats(raw: unknown): number[] {
  let seats: unknown = raw;
  if (typeof seats === 'string') {
    try { seats = JSON.parse(seats); } catch { seats = []; }
  }
  if (!Array.isArray(seats)) return [];
  const nums = seats
    .map((r) => parseInt(String(r), 10))
    .filter((r, i, arr) => Number.isInteger(r) && r >= 1 && arr.indexOf(r) === i);
  return nums.sort((a, b) => a - b);
}

const fmtDate = (d: string) =>
  d ? new Date(d).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

const inputSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: '#10182E',
    '& fieldset': { borderColor: C.border },
    '&:hover fieldset': { borderColor: C.primary },
    '&.Mui-focused fieldset': { borderColor: C.primary },
  },
  '& .MuiInputLabel-root': { color: C.textMuted },
  '& .MuiOutlinedInput-input': { color: C.text },
};

const RANK_TONE = ['#FFD24A', '#C7CEDB', '#E08A4B']; // gold / silver / bronze

/* ─────────────────────────── Leaderboard view ─────────────────────────── */
const LeaderboardView: React.FC<{ type: RaceType; enabled: boolean }> = ({ type, enabled }) => {
  const [data, setData] = useState<LeaderboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    // Race band hai — backend bhi 404 dega, to fetch hi mat karo. Shell upar
    // "switched off" wala banner already dikha raha hai.
    if (!enabled) { setData(null); setError(null); setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<{ success: boolean; data: LeaderboardData; error?: string }>(
        '/pramotion/leaderboard',
        { method: 'POST', body: JSON.stringify({ type, date: istNowISO() }) }
      );
      if (res.success) setData(res.data);
      else { setData(null); setError(res.error || 'Failed to fetch leaderboard'); }
    } catch (err: any) {
      setData(null);
      setError(err?.message?.includes('No active') ? `No active ${type} race right now` : 'Error connecting to server');
    } finally { setLoading(false); }
  }, [type, enabled]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button onClick={fetchData} startIcon={<Refresh fontSize="small" className={loading ? 'animate-spin' : ''} />}
          sx={{ color: C.textSecondary, bgcolor: C.cardLight, border: `1px solid ${C.border}`, textTransform: 'none',
            '&:hover': { bgcolor: C.cardHover, borderColor: C.primary } }}>
          Refresh
        </Button>
      </Box>

      {error && <Alert severity="warning" sx={{ mb: 2, bgcolor: alpha(C.warning, 0.08), color: C.warning, border: `1px solid ${alpha(C.warning, 0.3)}` }}>{error}</Alert>}

      {/* Race summary cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: 2, mb: 2.5 }}>
        <StatCard loading={loading} icon={<MilitaryTech />} iconBg={C.primary}
          title="Race ID" value={data ? `#${data.race_id}` : '—'} subtitle={`${type[0].toUpperCase()}${type.slice(1)} race`} />
        <StatCard loading={loading} icon={<EmojiEvents />} iconBg="#7B5EF5"
          title="Multiplier" value={data ? `${data.multiplier}x` : '—'} subtitle="Points multiplier" />
        <StatCard loading={loading} icon={<Groups />} iconBg={C.info}
          title="Players" value={data ? fmtNum(data.leaderboard.length) : '0'} subtitle="In this race" />
        <StatCard loading={loading} icon={<Schedule />} iconBg={C.warning}
          title="Ends" value={data ? fmtDate(data.end_time) : '—'} subtitle={data ? `Started ${fmtDate(data.start_time)}` : 'IST window'} />
      </Box>

      {/* Leaderboard table */}
      <Paper elevation={0} sx={{ borderRadius: 3, bgcolor: C.card, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 3, py: 2, borderBottom: `1px solid ${C.border}` }}>
          <EmojiEvents sx={{ fontSize: 18, color: C.primary }} />
          <Typography sx={{ color: C.text, fontWeight: 700, fontSize: '0.95rem' }}>Race Leaderboard</Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Rank', 'User ID', 'Player', 'Wager (USD)', 'Points'].map((h, i) => (
                  <TableCell key={h} align={i >= 3 ? 'right' : 'left'}
                    sx={{ color: C.textMuted, fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6, borderBottom: `1px solid ${C.border}` }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 5, borderBottom: 'none' }}><CircularProgress size={26} sx={{ color: C.primary }} /></TableCell></TableRow>
              ) : (!data || data.leaderboard.length === 0) ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 5, color: C.textMuted, borderBottom: 'none' }}>
                  {enabled ? 'No players in this race yet.' : `The ${type} race is switched off.`}
                </TableCell></TableRow>
              ) : (
                data.leaderboard.map((row) => {
                  const tone = row.rank <= 3 ? RANK_TONE[row.rank - 1] : C.textSecondary;
                  return (
                    <TableRow key={row.user_id} hover sx={{ '&:hover': { bgcolor: C.cardHover } }}>
                      <TableCell sx={{ borderBottom: `1px solid ${alpha(C.border, 0.5)}` }}>
                        <Chip label={`#${row.rank}`} size="small"
                          icon={row.rank <= 3 ? <MilitaryTech sx={{ fontSize: 14, color: `${tone} !important` }} /> : undefined}
                          sx={{ bgcolor: alpha(tone, 0.14), color: tone, fontWeight: 800, fontSize: 11 }} />
                      </TableCell>
                      <TableCell sx={{ color: C.textSecondary, fontSize: 13, borderBottom: `1px solid ${alpha(C.border, 0.5)}` }}>{row.user_id}</TableCell>
                      <TableCell sx={{ color: C.text, fontWeight: 600, fontSize: 13, borderBottom: `1px solid ${alpha(C.border, 0.5)}` }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          {row.name || `User ${row.user_id}`}
                          {row.is_boat && (
                            <Chip label="BOAT" size="small"
                              sx={{ height: 18, fontSize: 9.5, fontWeight: 800, letterSpacing: 0.4,
                                bgcolor: alpha(C.warning, 0.16), color: C.warning,
                                border: `1px solid ${alpha(C.warning, 0.35)}`,
                                '& .MuiChip-label': { px: 0.75 } }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ color: C.primaryLight, fontFamily: 'monospace', borderBottom: `1px solid ${alpha(C.border, 0.5)}` }}>${fmtNum(row.amount)}</TableCell>
                      <TableCell align="right" sx={{ color: C.success, fontWeight: 800, fontFamily: 'monospace', borderBottom: `1px solid ${alpha(C.border, 0.5)}` }}>{fmtNum(row.points)}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

/* ─────────────────────────── Config view ─────────────────────────── */
const ConfigView: React.FC<{ type: RaceType; enabled: boolean }> = ({ type, enabled }) => {
  const [sportsPoint, setSportsPoint] = useState('3.0');
  const [casinoPoint, setCasinoPoint] = useState('4.0');
  const [slotPoint, setSlotPoint] = useState('0');
  const [crashPoint, setCrashPoint] = useState('0');
  const [amount, setAmount] = useState('0');
  const [winnerCount, setWinnerCount] = useState('10');
  const [feePercent, setFeePercent] = useState('0');
  const [top3Percent, setTop3Percent] = useState('70');
  const [savedRanks, setSavedRanks] = useState<RankPreview[]>([]); // backend se aaya rank_percentage
  const [bookedEnabled, setBookedEnabled] = useState(false);
  const [bookedSeats, setBookedSeats] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadConfig = useCallback(async () => {
    setLoading(true); setError(null); setSuccess(null);
    try {
      const res = await apiFetch<{ success: boolean; data: RaceConfig | null }>(`/pramotion/config?type=${type}`);
      const cfg = res?.data;
      if (cfg) {
        setSportsPoint(String(cfg.sports_point));
        setCasinoPoint(String(cfg.casino_point));
        setSlotPoint(String(cfg.slot_point ?? 0));
        setCrashPoint(String(cfg.crash_point ?? 0));
        setAmount(String(cfg.amount));
        setWinnerCount(String(cfg.winner_count ?? 10));
        setFeePercent(String(cfg.platform_fee_percent ?? 0));
        setTop3Percent(String(cfg.top3_percentage ?? 70));
        setSavedRanks(Array.isArray(cfg.rank_percentage) ? cfg.rank_percentage : []);
        setBookedEnabled(Boolean(cfg.booked_seat_enabled));
        setBookedSeats(parseSeats(cfg.booked_seats));
      } else {
        setSportsPoint('3.0'); setCasinoPoint('4.0'); setSlotPoint('0'); setCrashPoint('0'); setAmount('0');
        setWinnerCount('10'); setFeePercent('0'); setTop3Percent('70'); setSavedRanks([]);
        setBookedEnabled(false); setBookedSeats([]);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load config');
    } finally { setLoading(false); }
  }, [type]);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  // Har point field valid number hona chahiye (khaali / NaN allowed nahi)
  const isNum = (v: string) => v.trim() !== '' && isFinite(Number(v));
  const pointFields = { sports: sportsPoint, casino: casinoPoint, slot: slotPoint, crash: crashPoint };
  const pointsValid = Object.values(pointFields).every(isNum);

  const poolAmount = parseFloat(amount) || 0;
  const wc = parseInt(winnerCount, 10);
  const feePct = parseFloat(feePercent);
  const top3Pct = parseFloat(top3Percent);
  const winnerValid = Number.isInteger(wc) && wc >= 1;
  const feeValid = isFinite(feePct) && feePct >= 0 && feePct <= 100;
  const top3Valid = isFinite(top3Pct) && top3Pct >= 0 && top3Pct <= 100;
  // rest (other) auto = 100 - top3
  const otherPct = top3Valid ? Number((100 - top3Pct).toFixed(2)) : 0;

  // booked-seat ranks selectable in 1..winnerCount range
  const toggleSeat = (r: number) =>
    setBookedSeats((prev) =>
      prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r].sort((a, b) => a - b)
    );

  // backend ne jo distribute kiya (saved rank_percentage ka total reward)
  const distributable = useMemo(
    () => savedRanks.reduce((s, r) => s + (Number(r.amount) || 0), 0),
    [savedRanks]
  );

  const handleSave = async () => {
    setError(null); setSuccess(null);
    const invalid = Object.entries(pointFields).filter(([, v]) => !isNum(v)).map(([k]) => k);
    if (invalid.length) { setError(`Point values must be valid numbers: ${invalid.join(', ')}`); return; }
    if (!winnerValid) { setError('Winner count must be a whole number ≥ 1'); return; }
    if (!feeValid) { setError('Platform fee must be a number between 0 and 100'); return; }
    if (!top3Valid) { setError('Top 3 share must be a number between 0 and 100'); return; }
    setSaving(true);
    try {
      const payload = {
        type,
        sports_point: parseFloat(sportsPoint),
        casino_point: parseFloat(casinoPoint),
        slot_point: parseFloat(slotPoint),
        crash_point: parseFloat(crashPoint),
        amount: poolAmount,
        winner_count: wc,
        platform_fee_percent: feePct,
        top3_percentage: top3Pct,
        booked_seat_enabled: bookedEnabled,
        // ranks beyond the winner count are meaningless — only send valid ones
        booked_seats: bookedEnabled ? bookedSeats.filter((r) => r <= wc) : [],
        // carry the on/off switch through — a config save must not turn a stopped race back on
        enabled,
      };
      const res = await apiFetch<{ success: boolean; error?: string }>('/pramotion/config', {
        method: 'POST', body: JSON.stringify(payload),
      });
      if (res.success) {
        // backend ne rank_percentage calculate kar diya — fresh config laao taaki table update ho
        await loadConfig();
        setSuccess(`${type[0].toUpperCase()}${type.slice(1)} config saved successfully`);
      } else {
        setError(res.error || 'Save failed');
      }
    } catch (err: any) {
      setError(err?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const sectionSx = { borderRadius: 3, bgcolor: C.card, border: `1px solid ${C.border}`, p: 3, mb: 2.5 };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: C.primary }} /></Box>;
  }

  return (
    <Box>
      {/* Point multipliers */}
      <Paper elevation={0} sx={sectionSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <SettingsIcon sx={{ fontSize: 18, color: C.primary }} />
          <Typography sx={{ color: C.text, fontWeight: 700, fontSize: '0.95rem' }}>Point Multipliers</Typography>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <TextField label="Sports Point (×)" type="number" size="small" fullWidth sx={inputSx}
            value={sportsPoint} onChange={(e) => setSportsPoint(e.target.value)}
            error={!isNum(sportsPoint)} helperText={!isNum(sportsPoint) ? 'Must be a number' : ' '}
            InputProps={{ startAdornment: <SportsEsports sx={{ fontSize: 18, color: C.textMuted, mr: 1 }} /> }} />
          <TextField label="Casino Point (×)" type="number" size="small" fullWidth sx={inputSx}
            value={casinoPoint} onChange={(e) => setCasinoPoint(e.target.value)}
            error={!isNum(casinoPoint)} helperText={!isNum(casinoPoint) ? 'Must be a number' : ' '}
            InputProps={{ startAdornment: <CasinoIcon sx={{ fontSize: 18, color: C.textMuted, mr: 1 }} /> }} />
          <TextField label="Slot Point (×)" type="number" size="small" fullWidth sx={inputSx}
            value={slotPoint} onChange={(e) => setSlotPoint(e.target.value)}
            error={!isNum(slotPoint)} helperText={!isNum(slotPoint) ? 'Must be a number' : ' '}
            InputProps={{ startAdornment: <SlotIcon sx={{ fontSize: 18, color: C.textMuted, mr: 1 }} /> }} />
          <TextField label="Crash Point (×)" type="number" size="small" fullWidth sx={inputSx}
            value={crashPoint} onChange={(e) => setCrashPoint(e.target.value)}
            error={!isNum(crashPoint)} helperText={!isNum(crashPoint) ? 'Must be a number' : ' '}
            InputProps={{ startAdornment: <CrashIcon sx={{ fontSize: 18, color: C.textMuted, mr: 1 }} /> }} />
        </Box>
      </Paper>

      {/* Prize pool · winners · platform fee */}
      <Paper elevation={0} sx={sectionSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AccountBalanceWallet sx={{ fontSize: 18, color: C.primary }} />
          <Typography sx={{ color: C.text, fontWeight: 700, fontSize: '0.95rem' }}>Prize Pool &amp; Winners</Typography>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
          <TextField label="Total Prize Pool (USD)" type="number" size="small" fullWidth sx={inputSx}
            value={amount} onChange={(e) => setAmount(e.target.value)}
            InputProps={{ startAdornment: <Typography sx={{ color: C.textMuted, mr: 0.5 }}>$</Typography> }} />
          <TextField label="Winner Count" type="number" size="small" fullWidth sx={inputSx}
            value={winnerCount} onChange={(e) => setWinnerCount(e.target.value)}
            error={!winnerValid} helperText={!winnerValid ? 'Whole number ≥ 1' : ' '}
            InputProps={{ startAdornment: <Groups sx={{ fontSize: 18, color: C.textMuted, mr: 1 }} /> }} />
          <TextField label="Platform Fee (%)" type="number" size="small" fullWidth sx={inputSx}
            value={feePercent} onChange={(e) => setFeePercent(e.target.value)}
            error={!feeValid} helperText={!feeValid ? '0 – 100' : ' '}
            InputProps={{ startAdornment: <PercentIcon sx={{ fontSize: 18, color: C.textMuted, mr: 1 }} /> }} />
          <TextField label="Top 3 Share (%)" type="number" size="small" fullWidth sx={inputSx}
            value={top3Percent} onChange={(e) => setTop3Percent(e.target.value)}
            error={!top3Valid} helperText={!top3Valid ? '0 – 100' : 'Rank 1-3 ka total share'}
            InputProps={{ startAdornment: <MilitaryTech sx={{ fontSize: 18, color: C.textMuted, mr: 1 }} /> }} />
          <TextField label="Other Share (auto)" size="small" fullWidth disabled
            sx={{ ...inputSx, '& .MuiOutlinedInput-input.Mui-disabled': { WebkitTextFillColor: C.textSecondary, fontWeight: 700 } }}
            value={top3Valid ? `${otherPct}%` : '—'}
            helperText="Rank 4+ → 100 − top 3"
            InputProps={{ startAdornment: <Groups sx={{ fontSize: 18, color: C.textMuted, mr: 1 }} /> }} />
        </Box>
      </Paper>

      {/* Booked seats (boats) */}
      <Paper elevation={0} sx={sectionSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SeatIcon sx={{ fontSize: 18, color: C.primary }} />
            <Typography sx={{ color: C.text, fontWeight: 700, fontSize: '0.95rem' }}>Booked Seats (Boats)</Typography>
          </Box>
          <FormControlLabel
            sx={{ mr: 0 }}
            control={<Switch checked={bookedEnabled} onChange={(e) => setBookedEnabled(e.target.checked)} />}
            label={<Typography sx={{ color: C.textSecondary, fontSize: 13, fontWeight: 600 }}>{bookedEnabled ? 'Enabled' : 'Disabled'}</Typography>}
          />
        </Box>

        <Typography sx={{ color: C.textMuted, fontSize: 12.5, mb: 2 }}>
          Reserve ranks for boats in the <strong>{type}</strong> race. A boat is auto-placed just above whoever
          currently holds each selected rank, and re-syncs every 10 minutes. Daily and weekly boats are tracked
          separately, so these ranks only affect the {type} leaderboard.
        </Typography>

        {!bookedEnabled ? (
          <Typography sx={{ color: C.textMuted, fontSize: 13, py: 0.5 }}>Enable to reserve ranks for boats.</Typography>
        ) : !winnerValid ? (
          <Typography sx={{ color: C.warning, fontSize: 13, py: 0.5 }}>Set a valid winner count first to pick ranks.</Typography>
        ) : (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {Array.from({ length: wc }, (_, i) => i + 1).map((r) => {
                const on = bookedSeats.includes(r);
                return (
                  <Chip key={r} label={`#${r}`} size="small" clickable onClick={() => toggleSeat(r)}
                    sx={{ fontWeight: 800, fontSize: 11,
                      bgcolor: on ? C.primary : C.cardLight,
                      color: on ? '#fff' : C.textSecondary,
                      border: `1px solid ${on ? C.primary : C.border}`,
                      '&:hover': { bgcolor: on ? C.primaryLight : C.cardHover } }} />
                );
              })}
            </Box>
            <Typography sx={{ color: C.textSecondary, fontSize: 12.5, mt: 2 }}>
              {bookedSeats.filter((r) => r <= wc).length > 0
                ? `Booked ranks: ${bookedSeats.filter((r) => r <= wc).map((r) => `#${r}`).join(', ')}`
                : 'No ranks selected — no boats will be placed.'}
            </Typography>
          </>
        )}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2, bgcolor: alpha(C.error, 0.08), color: C.error, border: `1px solid ${alpha(C.error, 0.3)}` }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, bgcolor: alpha(C.success, 0.08), color: C.success, border: `1px solid ${alpha(C.success, 0.3)}` }}>{success}</Alert>}

      {/* Save — upar, taaki save karte hi neeche table refresh ho jaye */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
        <Button variant="contained" onClick={handleSave} disabled={saving || !pointsValid || !winnerValid || !feeValid || !top3Valid}
          sx={{ bgcolor: C.primary, textTransform: 'none', fontWeight: 700, px: 3, '&:hover': { bgcolor: C.primaryLight }, '&.Mui-disabled': { bgcolor: alpha(C.primary, 0.3), color: alpha(C.text, 0.4) } }}>
          {saving ? 'Saving…' : `Save ${type[0].toUpperCase()}${type.slice(1)} Config`}
        </Button>
        {!pointsValid ? (
          <Typography variant="caption" sx={{ color: C.error }}>All point values must be valid numbers.</Typography>
        ) : !winnerValid ? (
          <Typography variant="caption" sx={{ color: C.error }}>Winner count must be a whole number ≥ 1.</Typography>
        ) : !feeValid ? (
          <Typography variant="caption" sx={{ color: C.error }}>Platform fee must be between 0 and 100.</Typography>
        ) : !top3Valid ? (
          <Typography variant="caption" sx={{ color: C.error }}>Top 3 share must be between 0 and 100.</Typography>
        ) : null}
      </Box>

      {/* Saved rank-wise distribution — backend (rank_percentage) se, save ke baad refresh hota hai */}
      <Paper elevation={0} sx={sectionSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MilitaryTech sx={{ fontSize: 18, color: C.primary }} />
            <Typography sx={{ color: C.text, fontWeight: 700, fontSize: '0.95rem' }}>Rank-wise Distribution</Typography>
          </Box>
          {savedRanks.length > 0 && (
            <Chip size="small" label={`${savedRanks.length} winners · Distributed: $${fmtNum(distributable)}`}
              sx={{ bgcolor: alpha(C.primary, 0.14), color: C.primaryLight, fontWeight: 700 }} />
          )}
        </Box>

        {savedRanks.length === 0 ? (
          <Typography sx={{ color: C.textMuted, fontSize: 13, py: 1 }}>Save the config to generate the rank-wise distribution.</Typography>
        ) : (
          <TableContainer sx={{ border: `1px solid ${C.border}`, borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Rank', 'Share %', 'Reward'].map((h, i) => (
                    <TableCell key={h} align={i === 0 ? 'left' : 'right'}
                      sx={{ color: C.textMuted, fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6, borderBottom: `1px solid ${C.border}` }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {savedRanks.map((row) => {
                  const tone = row.rank <= 3 ? RANK_TONE[row.rank - 1] : C.textSecondary;
                  return (
                    <TableRow key={row.rank} hover sx={{ '&:hover': { bgcolor: C.cardHover } }}>
                      <TableCell sx={{ borderBottom: `1px solid ${alpha(C.border, 0.5)}` }}>
                        <Chip label={`#${row.rank}`} size="small"
                          sx={{ bgcolor: alpha(tone, 0.14), color: tone, fontWeight: 800, fontSize: 11 }} />
                      </TableCell>
                      <TableCell align="right" sx={{ color: C.textSecondary, fontFamily: 'monospace', borderBottom: `1px solid ${alpha(C.border, 0.5)}` }}>{row.percentage}%</TableCell>
                      <TableCell align="right" sx={{ color: C.success, fontWeight: 800, fontFamily: 'monospace', borderBottom: `1px solid ${alpha(C.border, 0.5)}` }}>${fmtNum(row.amount)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

/* ─────────────────── Race on/off switch (page header) ─────────────────── */
const RaceSwitch: React.FC<{
  type: RaceType;
  enabled: boolean;
  busy: boolean;
  onToggle: (next: boolean) => void;
}> = ({ type, enabled, busy, onToggle }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', gap: 1.25, px: 1.75, py: 0.75, borderRadius: 2,
    bgcolor: C.card,
    border: `1px solid ${enabled ? alpha(C.success, 0.45) : alpha(C.error, 0.45)}`,
  }}>
    <PowerIcon sx={{ fontSize: 18, color: enabled ? C.success : C.error }} />
    <Box sx={{ lineHeight: 1.1 }}>
      <Typography sx={{ color: C.textMuted, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 700 }}>
        {type} race
      </Typography>
      <Typography sx={{ color: enabled ? C.success : C.error, fontSize: 13, fontWeight: 800 }}>
        {busy ? 'Saving…' : enabled ? 'Running' : 'Stopped'}
      </Typography>
    </Box>
    <Switch
      checked={enabled}
      disabled={busy}
      onChange={(e) => onToggle(e.target.checked)}
      sx={{ '& .Mui-checked': { color: C.success }, '& .Mui-checked + .MuiSwitch-track': { bgcolor: `${C.success} !important` } }}
    />
  </Box>
);

/* ─────────────────────────── Page shell ─────────────────────────── */
const Promotion: React.FC = () => {
  const [tab, setTab] = useState<TabKey>('leaderboard');
  const [type, setType] = useState<RaceType>('daily');
  // Race chal raha hai ya nahi — race_config.enabled se. Default true taaki
  // config load hone se pehle switch galat state na dikhaye.
  const [enabledMap, setEnabledMap] = useState<Record<RaceType, boolean>>({ daily: true, weekly: true });
  const [toggling, setToggling] = useState<RaceType | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);

  const loadEnabled = useCallback(async () => {
    try {
      const res = await apiFetch<{ success: boolean; data: RaceConfig[] }>('/pramotion/config');
      const rows = Array.isArray(res?.data) ? res.data : [];
      setEnabledMap({
        daily:  rows.find((r) => r.type === 'daily')?.enabled  !== false,
        weekly: rows.find((r) => r.type === 'weekly')?.enabled !== false,
      });
    } catch {
      /* config fetch fail — switch apni current state par rehne do */
    }
  }, []);

  useEffect(() => { loadEnabled(); }, [loadEnabled]);

  const handleToggle = async (raceType: RaceType, next: boolean) => {
    setToggling(raceType);
    setToggleError(null);
    // optimistic — fail hone par neeche revert ho jaata hai
    setEnabledMap((prev) => ({ ...prev, [raceType]: next }));
    try {
      const res = await apiFetch<{ success: boolean; error?: string }>('/pramotion/toggle', {
        method: 'POST', body: JSON.stringify({ type: raceType, enabled: next }),
      });
      if (!res.success) {
        setEnabledMap((prev) => ({ ...prev, [raceType]: !next }));
        setToggleError(res.error || `Could not turn the ${raceType} race ${next ? 'on' : 'off'}`);
      }
    } catch (err: any) {
      setEnabledMap((prev) => ({ ...prev, [raceType]: !next }));
      setToggleError(err?.message || `Could not turn the ${raceType} race ${next ? 'on' : 'off'}`);
    } finally {
      setToggling(null);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, minHeight: '100vh', bgcolor: C.bg }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 44, height: 44, borderRadius: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'linear-gradient(135deg, #2B6EF5 0%, #7B5EF5 100%)', boxShadow: `0 8px 24px ${alpha(C.primary, 0.35)}` }}>
            <EmojiEvents sx={{ color: '#fff' }} />
          </Box>
          <Box>
            <Typography sx={{ color: C.text, fontWeight: 800, fontSize: { xs: '1.3rem', sm: '1.5rem' }, lineHeight: 1.2 }}>Promotions</Typography>
            <Typography sx={{ color: C.textMuted, fontSize: '0.78rem' }}>Daily &amp; weekly wager races — leaderboard &amp; prize configuration (IST)</Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        {/* On/off switch for the race currently being viewed */}
        <RaceSwitch type={type} enabled={enabledMap[type]} busy={toggling === type}
          onToggle={(next) => handleToggle(type, next)} />

        {/* Daily / Weekly */}
        <ToggleButtonGroup value={type} exclusive size="small" onChange={(_, v) => v && setType(v)}
          sx={{
            bgcolor: C.card, border: `1px solid ${C.border}`, borderRadius: 2,
            '& .MuiToggleButton-root': { color: C.textMuted, border: 'none', textTransform: 'none', fontWeight: 600, px: 2.5,
              '&.Mui-selected': { bgcolor: alpha(C.primary, 0.15), color: C.primaryLight, '&:hover': { bgcolor: alpha(C.primary, 0.22) } } },
          }}>
          <ToggleButton value="daily">Daily</ToggleButton>
          <ToggleButton value="weekly">Weekly</ToggleButton>
        </ToggleButtonGroup>
        </Box>
      </Box>

      {toggleError && (
        <Alert severity="error" sx={{ mb: 2, bgcolor: alpha(C.error, 0.08), color: C.error, border: `1px solid ${alpha(C.error, 0.3)}` }}>
          {toggleError}
        </Alert>
      )}

      {!enabledMap[type] && (
        <Alert severity="warning" icon={<PowerIcon fontSize="small" />}
          sx={{ mb: 2, bgcolor: alpha(C.warning, 0.08), color: C.warning, border: `1px solid ${alpha(C.warning, 0.3)}` }}>
          The <strong>{type}</strong> race is switched off — no new race is created or settled, and players don't see it on the site.
          Turn it back on above to resume; the current window's race starts immediately.
        </Alert>
      )}

      {/* Tabs */}
      <Box sx={{ display: 'inline-flex', gap: 0.5, bgcolor: C.card, borderRadius: 2, p: 0.5, border: `1px solid ${C.border}`, mb: 3 }}>
        {([
          { key: 'leaderboard' as const, label: 'Leaderboard', icon: <EmojiEvents sx={{ fontSize: 16 }} /> },
          { key: 'config' as const, label: 'Config', icon: <SettingsIcon sx={{ fontSize: 16 }} /> },
        ]).map((t) => (
          <Button key={t.key} onClick={() => setTab(t.key)} startIcon={t.icon}
            sx={{ textTransform: 'none', fontWeight: 600, px: 2.5, borderRadius: 1.5,
              color: tab === t.key ? '#fff' : C.textMuted,
              bgcolor: tab === t.key ? C.primary : 'transparent',
              boxShadow: tab === t.key ? `0 4px 14px ${alpha(C.primary, 0.3)}` : 'none',
              '&:hover': { bgcolor: tab === t.key ? C.primaryLight : C.cardHover, color: tab === t.key ? '#fff' : C.text } }}>
            {t.label}
          </Button>
        ))}
      </Box>

      <Divider sx={{ display: 'none' }} />

      {tab === 'leaderboard'
        ? <LeaderboardView type={type} enabled={enabledMap[type]} />
        : <ConfigView type={type} enabled={enabledMap[type]} />}
    </Box>
  );
};

export default Promotion;
